var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// entities/mongoEntity.ts
import { A } from "@adornis/base/env-info";
import { NotAuthorizedError } from "../errors/NotAuthorizedError";
import { PermissionsNotSetError } from "../errors/PermissionsNotSetError";
import { combineLatest } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { Int } from "../baseqlTypes";
import { Arg, Entity, Field, Mutation, Query, Subscription } from "../decorators";
import {
  getCollection,
  getCollectionForClass,
  getCollectionHandle,
  getCollectionHandleForClass,
  withTransaction
} from "../server/collections";
import { entityPermissions, publishFilter } from "../server/context";
import { createModifier } from "../utils/helpers";
import { AdornisEntity, constructValue } from "./adornisEntity";
var MongoEntity = class extends AdornisEntity {
  static resolveID(entity) {
    return entity._id;
  }
  static getByID(id) {
    return async (gqlFields) => {
      let isArray = true;
      if (!Array.isArray(id)) {
        id = [id];
        isArray = false;
      }
      const result = await this.findEntityByID(this._class, id)(gqlFields);
      return isArray ? result : result[0];
    };
  }
  static subscribeByID(id) {
    return (gqlFields) => {
      let isArray = true;
      if (!Array.isArray(id)) {
        id = [id];
        isArray = false;
      }
      return this.subscribeEntityByID(
        this._class,
        id
      )(gqlFields).pipe(map((result) => isArray ? result : result[0]));
    };
  }
  static findEntityByID(entityClass, ids) {
    return async (gqlFields) => {
      const { collection, node } = await getCollectionForClass(entityClass);
      const selector = { _id: { $in: ids }, _class: { $in: this.validSubclasses(node) } };
      const filter = await publishFilter(entityClass);
      const rawEntities = await collection.find(filter ? { $and: [filter, selector] } : selector).toArray();
      return rawEntities.map(
        (entity) => constructValue(entity, { name: entityClass, type: node.type, arrayDepth: () => 0 })
      );
    };
  }
  static findAllEntities(entityClass, excludeIDs = []) {
    return async (gqlFields) => {
      const { collection, node } = await getCollectionForClass(entityClass);
      const selector = {
        _class: { $in: this.validSubclasses(node) }
      };
      if (excludeIDs.length)
        selector._id = { $nin: excludeIDs };
      const filter = await publishFilter(entityClass);
      const restrictedSelector = filter ? { $and: [filter, selector] } : selector;
      return collection.find(restrictedSelector).toArray();
    };
  }
  static countAllEntities(entityClass) {
    return async (gqlFields) => {
      const { collection, node } = await getCollectionForClass(entityClass);
      const selector = { _class: { $in: this.validSubclasses(node) } };
      const filter = await publishFilter(entityClass);
      return collection.countDocuments(filter ? { $and: [filter, selector] } : selector);
    };
  }
  static findEntityByText(entityClass, searchFields, searchText, regexOptions) {
    return async (gqlFields) => {
      const { collection, node } = await getCollectionForClass(entityClass);
      const selector = {
        _class: { $in: this.validSubclasses(node) },
        $or: searchFields.map((field) => ({
          [field]: { $regex: searchText, ...regexOptions ? { $options: regexOptions } : {} }
        }))
      };
      const filter = await publishFilter(entityClass);
      return collection.find(filter ? { $and: [filter, selector] } : selector).toArray();
    };
  }
  static subscribeEntityByID(entityClass, ids) {
    return (gqlFields) => {
      const steve = combineLatest([getCollectionHandleForClass(entityClass), publishFilter(entityClass)]).pipe(
        switchMap(([{ handle, node }, filter]) => {
          const selector = { _id: { $in: ids }, _class: { $in: this.validSubclasses(node) } };
          return handle.watchQuery(filter ? { $and: [filter, selector] } : selector);
        })
      );
      return steve;
    };
  }
  static subscribeAllEntities(entityClass, excludeIDs = []) {
    return (gqlFields) => {
      return combineLatest([getCollectionHandleForClass(entityClass), publishFilter(entityClass)]).pipe(
        switchMap(([{ handle, node }, filter]) => {
          const selector = {
            _class: { $in: this.validSubclasses(node) }
          };
          if (excludeIDs.length)
            selector._id = { $nin: excludeIDs };
          return handle.watchQuery(filter ? { $and: [filter, selector] } : selector);
        })
      );
    };
  }
  static subscribeEntityCount(entityClass, excludeIDs = []) {
    return (gqlFields) => {
      return combineLatest([getCollectionHandleForClass(entityClass), publishFilter(entityClass)]).pipe(
        switchMap(([{ handle, node }, filter]) => {
          const selector = {
            _class: { $in: this.validSubclasses(node) }
          };
          if (excludeIDs.length)
            selector._id = { $nin: excludeIDs };
          return handle.watchQuery(filter ? { $and: [filter, selector] } : selector, { projection: { _id: 1 } }).pipe(map((results) => results.length));
        })
      );
    };
  }
  static subscribePaginated(skip, limit, selector, options = {}) {
    if (!A.isServer)
      throw new Error("subscribePaginated is only accessible on the server");
    return combineLatest([getCollectionHandle(this._collectionName), publishFilter(this._class)]).pipe(
      switchMap(([handle, publishFilter2]) => {
        const selectorWithPublishFilter = {
          $and: [selector, ...publishFilter2 ? [publishFilter2] : []]
        };
        return handle.watchQuery(selectorWithPublishFilter, { skip, limit, ...options });
      })
    );
  }
  static subscribePaginatedAggregation(skip, limit, selector, aggregationStagesAfterSelection) {
    if (!A.isServer)
      throw new Error("subscribePaginatedAggregation is only accessible on the server");
    return this.subscribePaginated(skip, limit, selector).pipe(
      map((results) => results.map((x) => x._id)),
      switchMap(async (resultArray) => {
        const collection = await getCollection(this._collectionName);
        return await collection.aggregate([{ $match: { _id: { $in: resultArray } } }, ...aggregationStagesAfterSelection]).toArray();
      })
    );
  }
  static updateEntity(entityClass, entity) {
    return async (gqlFields) => {
      const permissions = await entityPermissions(entityClass);
      if (!permissions)
        throw new PermissionsNotSetError(entityClass);
      if (!permissions.modify)
        throw new NotAuthorizedError();
      const filter = await publishFilter(entityClass);
      if (!entity._id)
        throw new Error("Expected entity to have _id, but none was found.");
      const { collection } = await getCollectionForClass(entityClass);
      const old = await collection.findOne(filter ? { $and: [{ _id: entity._id }, filter] } : { _id: entity._id });
      if (!old)
        throw new Error("Could not find Entity to update.");
      if (typeof permissions?.modify === "function" && !await permissions.modify(old))
        throw new NotAuthorizedError();
      await entity.constructor.beforeUpdate(old, entity);
      const modifier = createModifier(entity.toJSON(), old);
      const result = await withTransaction(async (session) => {
        const updateResult = await collection.updateOne({ _id: entity._id }, { $set: modifier.$set }, { session });
        if (modifier.$push) {
          for (const pushOp of modifier.$push) {
            await collection.updateOne({ _id: entity._id }, { $push: pushOp }, { session });
          }
        }
        return updateResult;
      });
      await entity.constructor.afterUpdate(entity, result);
      return result.matchedCount;
    };
  }
  static createEntity(entityClass, entity) {
    return async (gqlFields) => {
      const permissions = await entityPermissions(entityClass);
      if (!permissions)
        throw new PermissionsNotSetError(entityClass);
      if (!permissions.create)
        throw new NotAuthorizedError();
      if (typeof permissions?.create === "function" && !await permissions.create(entity))
        throw new NotAuthorizedError();
      const { collection } = await getCollectionForClass(entityClass);
      if (!entity._id)
        entity._id = entity.constructor.resolveID(entity) || A.getGloballyUniqueID();
      const json = entity.toJSON();
      const result = await collection.insertOne(json);
      await entity.constructor.afterCreate(entity, result);
      return result.insertedId;
    };
  }
  static async beforeUpdate(oldEntity, update) {
  }
  static async afterUpdate(entity, result) {
  }
  static async afterCreate(entity, result) {
  }
  static removeEntity(entityClass, id) {
    return async (gqlFields) => {
      const permissions = await entityPermissions(entityClass);
      if (!permissions)
        throw new PermissionsNotSetError(entityClass);
      if (!permissions.delete)
        throw new NotAuthorizedError();
      const { collection } = await getCollectionForClass(entityClass);
      const entityData = await collection.findOne({ _id: id });
      if (typeof permissions?.delete === "function" && !await permissions.delete(entityData))
        throw new NotAuthorizedError();
      await this.beforeRemove(id, entityData);
      const result = await collection.deleteOne({ _id: id });
      return result.deletedCount === 1;
    };
  }
  static async beforeRemove(id, entityData) {
  }
  static findAll(excludeIDs) {
    return this.findAllEntities(this._class, excludeIDs);
  }
  static subscribeAll(excludeIDs) {
    return this.subscribeAllEntities(this._class, excludeIDs);
  }
  static count() {
    return this.countAllEntities(this._class);
  }
  static subscribeCount() {
    return this.countAllEntities(this._class);
  }
  async save() {
    if (!this._id)
      throw new Error("Use this.create() to create new entities, .save() is only for updating data.");
    await this.commit();
    const matchedCount = await this.constructor.updateEntity(
      this.constructor._class,
      this
    )("");
    await super.save();
    return matchedCount;
  }
  async create() {
    await super.commit();
    const _id = await this.constructor.createEntity(
      this.constructor._class,
      this
    )("");
    this._id = _id;
    await super.save();
    return _id;
  }
  async remove() {
    super.remove();
    return this.constructor.removeEntity(
      this.constructor._class,
      this._id
    )("");
  }
};
MongoEntity._collectionName = "";
MongoEntity._class = "MongoEntity";
__decorateClass([
  Field((type) => String, { default: (v) => v === void 0 ? null : v })
], MongoEntity.prototype, "_id", 2);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("ids", (type) => [String]))
], MongoEntity, "findEntityByID", 1);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "findAllEntities", 1);
__decorateClass([
  Query((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String))
], MongoEntity, "countAllEntities", 1);
__decorateClass([
  Query((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("searchFields", (type) => [String])),
  __decorateParam(2, Arg("searchText", (type) => String)),
  __decorateParam(3, Arg("regexOptions", (type) => String))
], MongoEntity, "findEntityByText", 1);
__decorateClass([
  Subscription((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("ids", (type) => [String]))
], MongoEntity, "subscribeEntityByID", 1);
__decorateClass([
  Subscription((type) => [MongoEntity]),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "subscribeAllEntities", 1);
__decorateClass([
  Subscription((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("excludeIDs", (type) => [String]))
], MongoEntity, "subscribeEntityCount", 1);
__decorateClass([
  Mutation((type) => Int),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("entity", (type) => MongoEntity))
], MongoEntity, "updateEntity", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("entity", (type) => MongoEntity))
], MongoEntity, "createEntity", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("entityClass", (type) => String)),
  __decorateParam(1, Arg("id", (type) => String))
], MongoEntity, "removeEntity", 1);
MongoEntity = __decorateClass([
  Entity()
], MongoEntity);
export {
  MongoEntity
};
